import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, LabelList } from "recharts";
import { adminApi } from "@/lib/api";
import { errText } from "@/lib/ranges";

// Pass 58 — Channel P&L. What each RM of ad spend returned in closed
// deals, per channel. The backend does every sum (channel_pnl.py); this
// only lays the block out.

// One series, one hue. A darker step of the brand gold: passes the
// palette validator's lightness, chroma and 3:1 contrast checks on the
// card surface. Values are written in text ink, never in this colour.
const BAR = "#8A6D1E";
const AMBER = "#8a6d3b";

const myr = (n) => (n === null || n === undefined ? "—" : `RM ${Math.round(Number(n)).toLocaleString("en-MY")}`);
const perRm = (n) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (v === 0) return "RM 0";
  return `RM ${v < 10 ? v.toFixed(2) : Math.round(v).toLocaleString("en-MY")}`;
};

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function Chip({ children, warn, testId }) {
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-block", fontSize: 11, padding: "4px 10px", marginRight: 8, marginBottom: 8,
        border: `1px solid ${warn ? "#e8d9c5" : "var(--scw-line)"}`,
        background: warn ? "#fcf8f4" : "#fff", color: warn ? AMBER : "#5C5C5C",
      }}
    >
      {children}
    </span>
  );
}

function ManualSpendForm({ channels, onSaved }) {
  const [month, setMonth] = useState(thisMonth());
  const [channel, setChannel] = useState(channels[0] || "OpenAI Ads");
  const [spend, setSpend] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setMsg(""); setErr("");
    const value = Number(String(spend).replace(/[^0-9.]/g, ""));
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) { setErr("Month must be YYYY-MM, e.g. 2026-09."); return; }
    if (!spend || Number.isNaN(value)) { setErr("Enter an amount."); return; }
    setBusy(true);
    try {
      await adminApi.manualSpend({ month, channel, spend_myr: value });
      setMsg(`Saved: ${channel}, ${month}, ${myr(value)}.`);
      setSpend("");
      onSaved();
    } catch (e) {
      setErr(errText(e, "Could not save."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "16px 18px", borderTop: "1px solid var(--scw-line)" }} data-testid="channels-manual-form">
      <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Monthly spend, entered by hand</p>
      <p style={{ fontSize: 11, color: "var(--scw-muted)", margin: "0 0 12px 0", lineHeight: 1.6 }}>
        For channels with no automatic feed (Meta and OpenAI ads). Use the Ads Manager figure before tax. Re-entering a month replaces it. Where a channel already has
        daily data for a month, the daily data is used and this figure is ignored.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <div>
          <label className="scw-label">Month</label>
          <input type="month" className="scw-input" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="channels-manual-month" />
        </div>
        <div>
          <label className="scw-label">Channel</label>
          <select className="scw-select" value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="channels-manual-channel">
            {channels.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="scw-label">Spend (RM)</label>
          <input className="scw-input" inputMode="decimal" value={spend} onChange={(e) => setSpend(e.target.value)} placeholder="290" style={{ width: 120 }} data-testid="channels-manual-spend" />
        </div>
        <button type="button" className="scw-btn" disabled={busy} onClick={save} data-testid="channels-manual-save">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
      {msg && <p style={{ fontSize: 12, marginTop: 10 }} data-testid="channels-manual-ok">{msg}</p>}
      {err && <p style={{ fontSize: 12, marginTop: 10, color: "#a13b3b" }} data-testid="channels-manual-err">{err}</p>}
    </div>
  );
}

const NOWRAP = { whiteSpace: "nowrap" };

function RowCells({ r, bold }) {
  return (
    <>
      <td style={NOWRAP}>{myr(r.spend)}</td>
      <td>{r.leads}</td>
      <td>{r.qualified}</td>
      <td>{r.won}</td>
      <td style={NOWRAP}>{myr(r.won_rm)}</td>
      <td style={NOWRAP}>{myr(r.cost_per_lead)}</td>
      <td style={NOWRAP}>{myr(r.cost_per_qualified)}</td>
      <td style={NOWRAP}>{myr(r.cost_per_won)}</td>
      <td style={{ ...NOWRAP, fontWeight: bold ? 600 : 500 }}>{perRm(r.revenue_per_rm)}</td>
    </>
  );
}

function freshText(f) {
  // Pass 59: Meta is entered monthly while its daily job is off; that is
  // the plan, not a stale feed.
  if (f.mode === "monthly") {
    return `${f.source} spend: entered monthly${f.last_written ? `, last entry ${f.last_written.slice(0, 10)}` : ""}`;
  }
  const when = f.last_written ? `last updated ${f.last_written.slice(0, 16)}` : "no data yet";
  return `${f.source} spend: ${when}${f.stale && f.last_written ? " (over 48h)" : ""}`;
}

export default function ChannelsPanel({ channels, onReload }) {
  if (!channels) return null;
  const rows = channels.rows || [];
  const chartData = rows
    .filter((r) => r.spend > 0 && r.revenue_per_rm !== null)
    .map((r) => ({ channel: r.channel, value: r.revenue_per_rm, low: r.low_n }));
  const fresh = channels.spend_freshness || [];

  return (
    <div className="scw-card" style={{ padding: 0, marginBottom: 32 }} data-testid="analytics-channels">
      <div style={{ padding: "16px 18px 4px 18px" }}>
        <div style={{ marginBottom: 10 }}>
          <Chip testId="channels-coverage">Attribution coverage: {channels.coverage_pct}%</Chip>
          {fresh.map((f) => (
            <Chip key={f.source} warn={f.stale} testId={`channels-fresh-${f.source}`}>{freshText(f)}</Chip>
          ))}
        </div>
        <p style={{ fontSize: 11, color: "var(--scw-muted)", margin: "0 0 6px 0", lineHeight: 1.6 }}>
          {channels.range.from} to {channels.range.to}. Deals are counted in the month the lead arrived. Recent months
          understate until their deals close. Revenue is not margin.
        </p>
        {channels.spend_status === "not_configured" && (
          <p style={{ fontSize: 12, color: AMBER, margin: "6px 0" }} data-testid="channels-not-configured">
            Spend data is not connected yet (CHANNEL_DATA_SHEET_ID). Leads and deals below are live; spend shows as zero.
          </p>
        )}
        {channels.spend_status === "error" && (
          <p style={{ fontSize: 12, color: "#a13b3b", margin: "6px 0" }} data-testid="channels-spend-error">
            Could not read the Channel Data sheet. Spend shows as zero until it can. {channels.spend_error}
          </p>
        )}
        {channels.meta_automatic && channels.meta_running && (
          <p style={{ fontSize: 12, color: AMBER, margin: "6px 0" }} data-testid="channels-meta-running">
            A Meta spend fetch is running. Figures update when it finishes.
          </p>
        )}
        {channels.meta_automatic && channels.meta_last_error && (
          <p style={{ fontSize: 12, color: AMBER, margin: "6px 0" }} data-testid="channels-meta-error">
            Last Meta spend run failed: {channels.meta_last_error}
          </p>
        )}
        {(channels.warnings || []).map((w) => (
          <p key={w} style={{ fontSize: 12, color: AMBER, margin: "6px 0" }}>{w}</p>
        ))}
      </div>

      {chartData.length > 0 && (
        <div style={{ padding: "8px 18px 0 18px" }} data-testid="channels-chart">
          <p className="scw-eyebrow" style={{ marginBottom: 4 }}>Won revenue per RM1 of spend</p>
          <p style={{ fontSize: 11, color: "var(--scw-muted)", margin: "0 0 8px 0" }}>
            Paler bars have fewer than 5 won deals: too few to judge.
          </p>
          <div style={{ height: Math.max(120, chartData.length * 44 + 30) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 72, bottom: 4, left: 8 }}>
                <CartesianGrid horizontal={false} stroke="#eaeaea" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#9a9a9a" }} axisLine={{ stroke: "#eaeaea" }} tickLine={false}
                  tickFormatter={(v) => `RM ${v}`} />
                <YAxis type="category" dataKey="channel" width={110} tick={{ fontSize: 12, fill: "#5C5C5C" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(92,92,92,0.06)" }}
                  formatter={(v, _n, item) => [`${perRm(v)} per RM1${item?.payload?.low ? " (too few deals to judge)" : ""}`, "Won revenue"]} />
                <Bar dataKey="value" barSize={20} radius={[0, 4, 4, 0]}>
                  {chartData.map((d) => <Cell key={d.channel} fill={BAR} fillOpacity={d.low ? 0.35 : 1} />)}
                  <LabelList dataKey="value" position="right" formatter={perRm} style={{ fontSize: 12, fill: "#3D3D3D" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="scw-table-wrap">
        <table className="scw-table" data-testid="channels-table">
          <thead><tr>
            <th>Channel</th><th>Spend</th><th>Leads</th><th>Qualified</th><th>Won</th><th>Won revenue</th>
            <th>Cost per lead</th><th>Cost per qualified</th><th>Cost per won</th><th>Revenue per RM1</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.channel} data-testid={`channels-row-${r.channel}`} style={{ opacity: r.low_n ? 0.6 : 1 }}>
                <td>
                  {r.channel}
                  {r.note && <div style={{ fontSize: 11, color: "var(--scw-muted)" }}>{r.note}</div>}
                  {r.low_n && r.spend > 0 && !r.note && (
                    <div style={{ fontSize: 11, color: "var(--scw-muted)" }}>Too few deals to judge</div>
                  )}
                </td>
                <RowCells r={r} />
              </tr>
            ))}
            <tr data-testid="channels-row-total" style={{ background: "#fafafa" }}>
              <td style={{ fontWeight: 600 }}>Total</td>
              <RowCells r={channels.total} bold />
            </tr>
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 11, color: "var(--scw-muted)", padding: "12px 18px 0 18px", margin: 0, lineHeight: 1.6 }}>
        Qualified: reached Contacted, In Progress or Closed Won at any point, which matches Google&apos;s Qualified Lead
        conversion. YouTube leads cannot be told apart from Google at lead level, so YouTube shows spend only. YouTube
        placements bought through Demand Gen or Performance Max count as Google spend. Instagram (bio link) is organic:
        visits through the profile link, including people who reached the profile from an ad.
      </p>

      <ManualSpendForm channels={channels.manual_channels || ["OpenAI Ads"]} onSaved={onReload} />
    </div>
  );
}
